import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Tool {
  title: string;
  description: string;
  icon: string;
  category: 'marketing' | 'finance';
  color: string;
  path?: string;
}

const tools: Tool[] = [
  // Marketing Tools
  { 
    title: 'Business Name Generator', 
    description: 'Generate creative and memorable business names based on your industry, values, and target audience.', 
    icon: '🚀',
    category: 'marketing',
    color: 'from-blue-400 to-indigo-500'
  },
  { 
    title: 'Business Plan Generator', 
    description: 'Create comprehensive business plans with executive summaries, market analysis, financial projections, and more.', 
    icon: '📝',
    category: 'marketing',
    color: 'from-green-400 to-teal-500',
    path: '/business-plan'
  },
  { 
    title: 'Pitch Deck Generator', 
    description: 'Generate professional pitch decks to present your business idea to investors with customizable slides and themes.', 
    icon: '📊',
    category: 'marketing',
    color: 'from-purple-400 to-violet-500',
    path: '/pitch-deck'
  },
  { 
    title: 'Logo Generator', 
    description: 'Create professional logos for your business with customizable templates and design elements.', 
    icon: '🖼',
    category: 'marketing',
    color: 'from-indigo-400 to-blue-500'
  },
  { 
    title: 'LinkedIn Profile Enhancer', 
    description: 'Optimize your LinkedIn profile with AI-generated professional summaries, headlines, and experience descriptions.', 
    icon: '👤',
    category: 'marketing',
    color: 'from-blue-500 to-blue-600'
  },
  { 
    title: 'LinkedIn Business Profile Enhancer', 
    description: 'Optimize your company\'s LinkedIn page with AI-generated content that attracts clients and showcases your business.', 
    icon: '👥',
    category: 'marketing',
    color: 'from-blue-600 to-blue-700'
  },
  { 
    title: 'Social Media Post Generator', 
    description: 'Create engaging social media content for multiple platforms including Facebook, LinkedIn, Twitter, and Reddit.', 
    icon: '📱',
    category: 'marketing',
    color: 'from-purple-400 to-indigo-500'
  },
  { 
    title: 'Text to Image Generator', 
    description: 'Create professional images for your social media and marketing materials using AI-powered image generation.', 
    icon: '🎨',
    category: 'marketing',
    color: 'from-pink-400 to-rose-500'
  },
  { 
    title: 'Press Release Generator', 
    description: 'Generate professional AP-style press releases to announce company news, product launches, and other milestones.', 
    icon: '📢',
    category: 'marketing',
    color: 'from-amber-400 to-orange-500'
  },
  { 
    title: 'Blog Post Creator', 
    description: 'Create engaging blog posts from a brief description to enhance your content marketing strategy.', 
    icon: '✏️',
    category: 'marketing',
    color: 'from-green-400 to-teal-500'
  },
  
  // Finance Tools
  { 
    title: 'Price Calculator', 
    description: 'Determine the optimal price for your products or services based on costs and market factors.', 
    icon: '💲',
    category: 'finance',
    color: 'from-blue-400 to-sky-500'
  },
  { 
    title: 'Profit Margin Analyzer', 
    description: 'Calculate and visualize your profit margins to ensure your business is financially healthy.', 
    icon: '📊',
    category: 'finance',
    color: 'from-violet-400 to-purple-500'
  },
  { 
    title: 'Break-Even Calculator', 
    description: 'Find out exactly how many sales you need to cover your costs and start making profit.', 
    icon: '⚖️',
    category: 'finance',
    color: 'from-teal-400 to-emerald-500'
  },
  { 
    title: 'Cash Flow Forecaster', 
    description: 'Project your cash flow for the next 3-6 months and identify potential cash shortfalls.', 
    icon: '📈',
    category: 'finance',
    color: 'from-green-400 to-emerald-500'
  },
  { 
    title: 'Pricing Strategy Advisor', 
    description: 'Get AI-powered recommendations for pricing strategies based on your business type and goals.', 
    icon: '📋',
    category: 'finance',
    color: 'from-blue-500 to-indigo-600'
  },
  { 
    title: 'Supplier Finder', 
    description: 'Find licensed trade suppliers for your products and services in your preferred location.', 
    icon: '🔍',
    category: 'finance',
    color: 'from-purple-400 to-pink-500'
  },
  { 
    title: 'Risk Manager', 
    description: 'Identify, assess and manage potential business risks to protect your investment and ensure long-term success.', 
    icon: '⚠️',
    category: 'finance',
    color: 'from-amber-500 to-red-500'
  },
];

const marketingTools = tools.filter(tool => tool.category === 'marketing');
const financeTools = tools.filter(tool => tool.category === 'finance');

const ToolGrid: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'marketing' | 'finance'>('marketing');
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);
  
  // Function to handle navigation when a tool is clicked
  const handleToolClick = (path?: string) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')] bg-[size:50px_50px]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 left-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 right-12 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      
        {/* Section Title with 3D effect */}
        <div className="relative mb-16 text-center">
          <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 inline-block mb-2">
            Powerful Tools
          </h2>
          <div className="h-1 w-40 mx-auto rounded-full bg-gradient-to-r from-blue-400 to-purple-500"></div>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">Boost your business with our specialized tools for marketing and finance.</p>
        </div>
        
        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="flex p-1 rounded-xl bg-gray-200/50 backdrop-blur-sm">
            <button
              onClick={() => setActiveTab('marketing')}
              className={`
                px-6 py-3 text-sm font-medium rounded-lg transition-all duration-300
                ${activeTab === 'marketing' 
                  ? 'bg-white text-blue-600 shadow-md' 
                  : 'text-gray-600 hover:bg-white/50'}
              `}
            >
              Marketing Tools
            </button>
            <button
              onClick={() => setActiveTab('finance')}
              className={`
                px-6 py-3 text-sm font-medium rounded-lg transition-all duration-300
                ${activeTab === 'finance' 
                  ? 'bg-white text-blue-600 shadow-md' 
                  : 'text-gray-600 hover:bg-white/50'}
              `}
            >
              Finance Tools
            </button>
          </div>
        </div>
        
        {/* Tools Grid with 3D Cards and Glass Morphism */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-500"
             style={{ 
               transform: activeTab === 'marketing' ? 'translateX(0)' : 'translateX(-100%)',
               opacity: activeTab === 'marketing' ? 1 : 0,
               position: activeTab === 'marketing' ? 'relative' : 'absolute',
               display: activeTab === 'marketing' ? 'grid' : 'none'
             }}
        >
          {marketingTools.map((tool, index) => (
            <div 
              key={tool.title} 
              className="relative transform transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              onMouseEnter={() => setHoveredTool(tool.title)}
              onMouseLeave={() => setHoveredTool(null)}
              onClick={() => handleToolClick(tool.path)}
            >
              {/* Card with glass morphism */}
              <div className="
                relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg 
                border border-white/30 shadow-lg group transition-all duration-300
                hover:shadow-xl p-6 h-full
              ">
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${tool.color} opacity-10 group-hover:opacity-20 transition-opacity`}></div>
                
                {/* Icon */}
                <div className={`
                  w-12 h-12 rounded-lg mb-4 flex items-center justify-center text-white 
                  bg-gradient-to-br ${tool.color} shadow-lg group-hover:scale-110 transition-transform
                `}>
                  <span className="text-2xl">{tool.icon}</span>
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                  {tool.title}
                </h3>
                <p className="text-gray-600 group-hover:text-gray-800 transition-colors">
                  {tool.description}
                </p>
                
                {/* Hidden action button that appears on hover */}
                <div className="
                  absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 
                  transition-opacity transform group-hover:translate-x-0 translate-x-4
                ">
                  <button className="
                    flex items-center justify-center w-8 h-8 rounded-full 
                    bg-white/80 text-blue-600 shadow-md hover:bg-white transition-colors
                  ">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Finance Tools Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-500"
             style={{ 
               transform: activeTab === 'finance' ? 'translateX(0)' : 'translateX(100%)',
               opacity: activeTab === 'finance' ? 1 : 0,
               position: activeTab === 'finance' ? 'relative' : 'absolute',
               display: activeTab === 'finance' ? 'grid' : 'none'
             }}
        >
          {financeTools.map((tool, index) => (
            <div 
              key={tool.title} 
              className="relative transform transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              onMouseEnter={() => setHoveredTool(tool.title)}
              onMouseLeave={() => setHoveredTool(null)}
              onClick={() => handleToolClick(tool.path)}
            >
              {/* Card with glass morphism */}
              <div className="
                relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg 
                border border-white/30 shadow-lg group transition-all duration-300
                hover:shadow-xl p-6 h-full
              ">
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${tool.color} opacity-10 group-hover:opacity-20 transition-opacity`}></div>
                
                {/* Icon */}
                <div className={`
                  w-12 h-12 rounded-lg mb-4 flex items-center justify-center text-white 
                  bg-gradient-to-br ${tool.color} shadow-lg group-hover:scale-110 transition-transform
                `}>
                  <span className="text-2xl">{tool.icon}</span>
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                  {tool.title}
                </h3>
                <p className="text-gray-600 group-hover:text-gray-800 transition-colors">
                  {tool.description}
                </p>
                
                {/* Hidden action button that appears on hover */}
                <div className="
                  absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 
                  transition-opacity transform group-hover:translate-x-0 translate-x-4
                ">
                  <button className="
                    flex items-center justify-center w-8 h-8 rounded-full 
                    bg-white/80 text-blue-600 shadow-md hover:bg-white transition-colors
                  ">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ToolGrid;
